import React, { Suspense, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Leaf, 
  TreePine, 
  Award, 
  BookOpen, 
  Users, 
  Building2,
  ArrowLeft,
  GraduationCap,
  Hammer,
  Lightbulb,
  Shield,
  Home,
  Sprout,
  Wind,
  Sun,
  Recycle
} from 'lucide-react';
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
  Stars,
  PerspectiveCamera
} from "@react-three/drei";

// Préchargement du modèle (tu peux changer le modèle 3D)
useGLTF.preload("/models/coffee_shop_isometric.glb");

// Modèle 3D
function TreeHouseModel() {
  const { scene } = useGLTF("/models/coffee_shop_isometric.glb");
  const ref = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    ref.current.rotation.y = t * 0.15;
  });
  
  return (
    <primitive
      ref={ref}
      object={scene}
      scale={[0.5, 0.5, 0.5]}
      position={[0, -1, 0]}
    />
  );
}

// Scène 3D
function Scene3D() {
  return (
    <Canvas
      shadows
      className="fixed inset-0 -z-10"
      dpr={[1, 2]}>
      <color attach="background" args={["#1a2e1a"]} />
      <fog attach="fog" args={["#1a2e1a", 5, 15]} />
      <ambientLight intensity={0.5} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
      
      <Suspense fallback={null}>
        <PerspectiveCamera makeDefault position={[0, 1, 10]} fov={40} />
        
        <Stage environment="forest">
          <Center>
            <TreeHouseModel />
          </Center>
        </Stage>
        
        <Environment preset="forest" />
        <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.3} />
        <Stars count={5000} depth={50} fade speed={1.5} />
      </Suspense>
    </Canvas>
  );
}

const GreenFactory = () => {
  const formations = [
    {
      icon: <Hammer className="w-8 h-8 text-green-400" />,
      title: "CAP Ouvrier Professionnel en Écoconstruction (OPEC)",
      duration: "2 ans",
      niveau: "Niveau 3 (CAP)",
      description: "Formation aux techniques de construction écologique utilisant des matériaux biosourcés et géosourcés pour bâtir durablement.",
      competences: [
        "Préparation et organisation de chantiers écoresponsables",
        "Réalisation d'ouvrages de maçonnerie avec matériaux biosourcés",
        "Construction d'ossatures bois issues de filières locales",
        "Isolation étanche à l'air avec matériaux bio et géosourcés"
      ]
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-emerald-400" />,
      title: "Bachelor Chargé de Projet Énergie et Bâtiment Durables",
      duration: "1 an en alternance",
      niveau: "Niveau 6 (Bac+3)",
      description: "Formation professionnelle pour accompagner la transition énergétique et gérer des projets de rénovation performante.",
      competences: [
        "Conseil et études pour bâtiments performants et confortables",
        "Développement d'installations d'énergies renouvelables",
        "Accompagnement à la transition énergétique des territoires",
        "Maîtrise d'œuvre thermique du bâtiment et fluides"
      ]
    }
  ];

  const debouches = [
    {
      icon: <Hammer className="w-6 h-6 text-green-400" />,
      title: "Ouvrier en écoconstruction",
      description: "Spécialiste de la construction écologique et durable"
    },
    {
      icon: <Building2 className="w-6 h-6 text-emerald-400" />,
      title: "Maçon en écomatériaux",
      description: "Expert en maçonnerie avec matériaux biosourcés"
    },
    {
      icon: <TreePine className="w-6 h-6 text-lime-400" />,
      title: "Charpentier écoconstructeur",
      description: "Construction d'ossatures bois écologiques"
    },
    {
      icon: <Shield className="w-6 h-6 text-teal-400" />,
      title: "Isolateur en écoconstruction",
      description: "Spécialiste de l'isolation thermique écologique"
    },
    {
      icon: <Lightbulb className="w-6 h-6 text-yellow-400" />,
      title: "Chargé de projet énergie",
      description: "Expert en transition énergétique et rénovation"
    },
    {
      icon: <Sun className="w-6 h-6 text-orange-400" />,
      title: "Conseiller en énergies renouvelables",
      description: "Accompagnement sur les solutions énergétiques durables"
    },
    {
      icon: <Home className="w-6 h-6 text-cyan-400" />,
      title: "Technicien en bureau d'études",
      description: "Études thermiques et fluides pour bâtiments durables"
    },
    {
      icon: <Wind className="w-6 h-6 text-sky-400" />,
      title: "Chargé de mission énergie",
      description: "Gestion de projets énergétiques en collectivité"
    }
  ];

  const avantages = [
    {
      icon: <Sprout className="w-6 h-6 text-green-400" />,
      title: "Métiers d'avenir",
      description: "Secteur en pleine croissance face aux enjeux climatiques"
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-emerald-400" />,
      title: "Formation innovante",
      description: "Apprentissage des techniques de pointe en écoconstruction"
    },
    {
      icon: <Award className="w-6 h-6 text-lime-400" />,
      title: "Impact positif",
      description: "Contribuer activement à la transition écologique"
    },
    {
      icon: <Recycle className="w-6 h-6 text-teal-400" />,
      title: "Approche durable",
      description: "Travail avec des matériaux locaux et écologiques"
    }
  ];

  const valeurs = [
    {
      icon: <Leaf className="w-8 h-8 text-green-300" />,
      title: "Respect de l'environnement",
      description: "Utilisation de matériaux biosourcés et géosourcés pour minimiser l'impact écologique"
    },
    {
      icon: <Sun className="w-8 h-8 text-yellow-300" />,
      title: "Efficacité énergétique",
      description: "Conception de bâtiments performants et économes en énergie"
    },
    {
      icon: <Users className="w-8 h-8 text-blue-300" />,
      title: "Engagement collectif",
      description: "Former les professionnels de demain pour bâtir durablement"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-40 bg-gradient-to-br from-green-950 to-emerald-900"
    >
      {/* Scène 3D en arrière-plan */}
      <div className="fixed inset-0 z-0">
        <Scene3D />
      </div>
      
      <Loader />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Bouton retour */}
        <Link to="/">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mb-8 px-6 py-3 bg-green-800/60 backdrop-blur-sm text-white rounded-lg flex items-center space-x-2 hover:bg-green-700/60 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </motion.button>
        </Link>

        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 10, 0],
                scale: [1, 1.1, 1, 1.1, 1]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 2
              }}
            >
              <Leaf className="w-16 h-16 text-green-400" />
            </motion.div>
            <h1 className="text-5xl font-bold text-white">Green Factory</h1>
          </div>
          <p className="text-xl text-green-100 max-w-3xl mx-auto mb-4">
            Le nouveau pôle écoconstruction du CFA Est Loiret
          </p>
          <p className="text-lg text-green-200 max-w-2xl mx-auto italic">
            Former les professionnels de demain pour bâtir durablement et répondre aux enjeux climatiques
          </p>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
            className="mt-6 inline-block px-6 py-2 bg-green-600/30 backdrop-blur-sm rounded-full border border-green-400/30"
          >
            <span className="text-green-100 font-semibold">Parce que former, c'est déjà agir ! 💪</span>
          </motion.div>
        </motion.div>

        {/* Valeurs Green Factory */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Nos Valeurs</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {valeurs.map((valeur, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.05 }}
                className="bg-green-800/40 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-green-500/20 transition-all border border-green-600/30"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-green-700/50 rounded-full">
                    {valeur.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{valeur.title}</h3>
                <p className="text-green-100 text-sm">{valeur.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Formations */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Nos Formations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {formations.map((formation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="bg-green-800/40 backdrop-blur-sm p-8 rounded-lg border-l-4 border-green-500 hover:shadow-lg hover:shadow-green-500/20 transition-all"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="p-3 bg-green-700/50 rounded-lg">
                    {formation.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{formation.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-3 py-1 bg-green-600/30 text-green-300 rounded-full text-sm border border-green-400/30">
                        {formation.duration}
                      </span>
                      <span className="px-3 py-1 bg-emerald-600/30 text-emerald-300 rounded-full text-sm border border-emerald-400/30">
                        {formation.niveau}
                      </span>
                    </div>
                  </div>
                </div>
                
                <p className="text-green-100 mb-6">{formation.description}</p>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center space-x-2">
                    <BookOpen className="w-5 h-5 text-green-400" />
                    <span>Compétences développées :</span>
                  </h4>
                  <ul className="space-y-2">
                    {formation.competences.map((comp, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-green-100">
                        <span className="text-green-400 mt-1">•</span>
                        <span>{comp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Débouchés */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Débouchés Professionnels</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {debouches.map((debouche, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-green-800/40 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-green-500/20 transition-all border border-green-600/30"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-green-700/50 rounded-lg">
                    {debouche.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{debouche.title}</h3>
                <p className="text-green-100 text-sm">{debouche.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Avantages */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Pourquoi choisir la Green Factory ?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {avantages.map((avantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-green-800/40 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-green-500/20 transition-all border border-green-600/30"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-green-700/50 rounded-lg">
                    {avantage.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{avantage.title}</h3>
                <p className="text-green-100 text-sm">{avantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Localisation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="max-w-4xl mx-auto mb-20"
        >
          <div className="bg-green-800/40 backdrop-blur-sm p-8 rounded-lg border border-green-600/30">
            <div className="flex items-center space-x-3 mb-4">
              <Building2 className="w-8 h-8 text-green-400" />
              <h2 className="text-2xl font-bold text-white">Localisation</h2>
            </div>
            <div className="text-green-100 space-y-2">
              <p className="text-lg font-semibold">Maison Feuillette</p>
              <p>1 Place de la République</p>
              <p>45200 Montargis</p>
              <p className="mt-4 text-sm">
                <span className="font-semibold">Contact :</span>{' '}
                <a href="mailto:greenfactory@cfa-montargis.com" className="text-green-300 hover:text-green-200 underline">
                  greenfactory@cfa-montargis.com
                </a>
              </p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
          className="text-center space-y-4"
        >
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-lg font-semibold shadow-lg"
            >
              Nous contacter pour plus d'informations
            </motion.button>
          </Link>
          <p className="text-green-200 text-sm">
            Rejoignez le mouvement de la construction durable ! 🌱
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default GreenFactory;